import { APIClient } from "src/lib/api-client";
import { UserProfile, ApiUserProfile } from "./types";
import { mapApiUserProfile } from "./mappers";

const PROFILE_DEDUPE_WINDOW_MS = 2000;
export const E2E_FEATURE_FLAGS_COOKIE = "caltana_e2e_feature_flags";

let browserProfileRequest: Promise<UserProfile> | null = null;
let browserProfileSnapshot: {
  expiresAt: number;
  profile: UserProfile;
} | null = null;

function isE2eFeatureFlagOverrideAllowed(): boolean {
  return process.env.NODE_ENV === "test" || process.env.NEXT_PUBLIC_VERCEL_ENV === "local";
}

export function parseE2eFeatureFlagOverrides(
  rawCookieValue: string | null | undefined,
): Record<string, boolean> {
  if (!rawCookieValue) {
    return {};
  }

  try {
    const decodedValue = decodeURIComponent(rawCookieValue);
    const parsedValue = JSON.parse(decodedValue);

    if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsedValue).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === "boolean",
      ),
    );
  } catch {
    return {};
  }
}

function getBrowserCookieValue(cookieName: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${cookieName}=`;
  const cookie = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));

  return cookie ? cookie.slice(prefix.length) : null;
}

async function getServerCookieValue(cookieName: string): Promise<string | null> {
  if (typeof window !== "undefined") {
    return null;
  }

  try {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    return cookieStore.get(cookieName)?.value ?? null;
  } catch {
    return null;
  }
}

async function getE2eFeatureFlagOverrides(): Promise<Record<string, boolean>> {
  if (!isE2eFeatureFlagOverrideAllowed()) {
    return {};
  }

  const rawCookieValue =
    typeof window === "undefined"
      ? await getServerCookieValue(E2E_FEATURE_FLAGS_COOKIE)
      : getBrowserCookieValue(E2E_FEATURE_FLAGS_COOKIE);

  return parseE2eFeatureFlagOverrides(rawCookieValue);
}

async function applyE2eFeatureFlagOverrides(profile: UserProfile): Promise<UserProfile> {
  const featureFlagOverrides = await getE2eFeatureFlagOverrides();

  if (Object.keys(featureFlagOverrides).length === 0) {
    return profile;
  }

  return {
    ...profile,
    featureFlags: {
      ...profile.featureFlags,
      ...featureFlagOverrides,
    },
  };
}

/**
 * Fetches the current user's profile
 * @returns Promise with the user profile
 */
export async function getProfile(): Promise<UserProfile> {
  if (typeof window !== "undefined") {
    const now = Date.now();

    if (browserProfileSnapshot && browserProfileSnapshot.expiresAt > now) {
      return browserProfileSnapshot.profile;
    }

    if (browserProfileRequest) {
      return browserProfileRequest;
    }
  }

  const client = new APIClient();
  const request = client
    .fetch<ApiUserProfile>(`/api/v1/profile/`)
    .then(mapApiUserProfile)
    .then(applyE2eFeatureFlagOverrides);

  if (typeof window === "undefined") {
    return request;
  }

  browserProfileRequest = request
    .then((profile) => {
      browserProfileSnapshot = {
        expiresAt: Date.now() + PROFILE_DEDUPE_WINDOW_MS,
        profile,
      };
      return profile;
    })
    .finally(() => {
      browserProfileRequest = null;
    });

  return browserProfileRequest;
}
